import Reveal from "@/components/Reveal";
import VocabTag from "@/components/VocabTag";
import { useBriefing } from "@/context/BriefingContext";
import { GIGA_IMAGES } from "@/lib/images";

/**
 * Closing CTA — dark poster section.
 * Heavy darkened abstract-systems image as backdrop; bone type overlaid.
 * Large typographic finale + discrete briefing CTA.
 */
export default function ClosingCTA() {
  const { openBriefing } = useBriefing();

  return (
    <section
      id="engage"
      data-testid="closing-cta-section"
      className="relative g1-inverse border-b border-[color:var(--g1-rule)] overflow-hidden"
    >
      {/* Backdrop image */}
      <div className="absolute inset-0" aria-hidden>
        <img
          src={GIGA_IMAGES.systemsField}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            filter: "grayscale(1) contrast(1.15) brightness(0.28) sepia(0.03)",
          }}
          loading="lazy"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(20,20,19,0.78) 0%, rgba(20,20,19,0.88) 60%, rgba(20,20,19,0.95) 100%)",
          }}
        />
      </div>

      {/* Content */}
      <div className="relative max-w-[1500px] mx-auto px-5 md:px-10 lg:px-14 py-28 md:py-40 lg:py-52">
        {/* Section head */}
        <div className="grid grid-cols-12 gap-8 mb-12 md:mb-16">
          <div className="col-span-6 md:col-span-3 lg:col-span-2">
            <Reveal>
              <div className="g1-label text-[color:var(--g1-bone)]/70">
                SEC. 08
              </div>
              <div className="g1-eyebrow mt-2 text-[color:var(--g1-bone)]">
                Engage
              </div>
            </Reveal>
          </div>
          <div className="col-span-6 md:col-span-3 lg:col-span-2 md:col-start-10 lg:col-start-11 text-right flex flex-col items-end gap-2">
            <Reveal>
              <VocabTag kind="FUSED" dark />
            </Reveal>
          </div>
        </div>

        {/* POSTER 3 — brutalist mono/serif lock-up */}
        <Reveal delay={80}>
          <div className="border-t border-b border-[color:var(--g1-bone)]/30 py-10 md:py-16">
            <h2
              data-testid="closing-cta-heading"
              className="font-mono font-medium uppercase text-[40px] sm:text-[56px] md:text-[88px] lg:text-[112px] xl:text-[128px] leading-[0.95] tracking-[-0.02em] text-[color:var(--g1-bone)]"
            >
              Lights out. <br />
              <span className="font-serif italic normal-case tracking-tight text-[color:var(--g1-olive-muted)]">
                On shore.
              </span>
            </h2>
          </div>
        </Reveal>

        {/* Support + CTA */}
        <div className="grid grid-cols-12 gap-8 mt-14 md:mt-20">
          <div className="col-span-12 md:col-span-7">
            <Reveal delay={140}>
              <p className="font-serif italic text-2xl md:text-3xl leading-snug text-[color:var(--g1-bone)] max-w-[38ch]">
                Briefings are granted on a reviewed basis — to investors,
                strategic partners, and policy stakeholders.
              </p>
            </Reveal>
          </div>

          <div className="col-span-12 md:col-span-5 md:col-start-8 flex md:justify-end items-start">
            <Reveal delay={220}>
              <div className="flex flex-col gap-3 w-full md:w-auto">
                <button
                  type="button"
                  data-testid="closing-request-briefing"
                  onClick={openBriefing}
                  className="g1-btn g1-btn-primary"
                  style={{
                    background: "var(--g1-bone)",
                    color: "var(--g1-ink)",
                    borderColor: "var(--g1-bone)",
                  }}
                >
                  Request a Briefing
                  <span className="g1-btn-arrow" aria-hidden>
                    →
                  </span>
                </button>
                <a
                  href="mailto:briefings@giga.one"
                  data-testid="closing-email-link"
                  className="g1-btn"
                  style={{
                    background: "transparent",
                    color: "var(--g1-bone)",
                    borderColor: "rgba(244,243,240,0.35)",
                  }}
                >
                  briefings@giga.one
                  <span className="g1-btn-arrow" aria-hidden>
                    →
                  </span>
                </a>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Bottom rail */}
        <Reveal delay={280}>
          <div className="mt-16 md:mt-24 pt-8 border-t border-[color:var(--g1-bone)]/30 grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div>
              <div className="g1-label text-[color:var(--g1-bone)]/60">
                Audience
              </div>
              <p className="font-sans text-[14px] mt-2 text-[color:var(--g1-bone)]">
                Investors · Strategic partners · Policy stakeholders
              </p>
            </div>
            <div>
              <div className="g1-label text-[color:var(--g1-bone)]/60">
                Cadence
              </div>
              <p className="font-sans text-[14px] mt-2 text-[color:var(--g1-bone)]">
                Briefings held on a reviewed basis
              </p>
            </div>
            <div>
              <div className="g1-label text-[color:var(--g1-bone)]/60">
                Discretion
              </div>
              <p className="font-sans text-[14px] mt-2 text-[color:var(--g1-bone)]">
                All communications in confidence
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
